import { useState } from 'react';

import { Spinner, Typography } from '@/components';
import typo from '@/components/typography/Typography.module.css';
import { useTeamMemberBrandSettingsUpdate } from '@/hooks';
import { useTranslation } from 'react-i18next';

import styles from './TeamBrandSettingsSection.module.css';

type BrandSettingField = 'want_email_notifications' | 'show_campaigns';

interface TeamBrandSettingsSectionProps {
  isAdmin: boolean;
  members: Api.Member[];
  setMembers: React.Dispatch<React.SetStateAction<Api.Member[]>>;
  teamId: string;
  viewerUserId: string;
}

export function TeamBrandSettingsSection({
  isAdmin,
  members,
  setMembers,
  teamId,
  viewerUserId
}: TeamBrandSettingsSectionProps) {
  const { t } = useTranslation();
  const [loadingCell, setLoadingCell] = useState<string | null>(null);

  const { mutation: patchBrandSettings } = useTeamMemberBrandSettingsUpdate({
    teamId,
    onSuccess: (response: Api.Member | ResponseObject) => {
      if ('user_id' in response && !('code' in response)) {
        setMembers((prev) =>
          prev.map((m) => (m.user_id === response.user_id ? { ...m, ...response } : m))
        );
      }
    }
  });

  if (!teamId) {
    return null;
  }

  const membersWithBrandRows = members.filter((m) => {
    if (m.status !== 'MEMBER' || (m.brand_settings?.length ?? 0) === 0) {
      return false;
    }
    if (isAdmin) {
      return true;
    }
    return m.user_id === viewerUserId;
  });

  function cellKey(memberId: string, brandId: string, field: BrandSettingField) {
    return `${memberId}:${brandId}:${field}`;
  }

  function canEditMember(member: Api.Member) {
    return viewerUserId === member.user_id || isAdmin;
  }

  function handleToggle(
    member: Api.Member,
    brandId: string,
    field: BrandSettingField,
    checked: boolean
  ) {
    if (!canEditMember(member)) {
      return;
    }
    const key = cellKey(member.user_id, brandId, field);
    setLoadingCell(key);
    patchBrandSettings(
      {
        userId: member.user_id,
        brandId,
        ...(field === 'want_email_notifications'
          ? { want_email_notifications: checked }
          : { show_campaigns: checked })
      },
      { onSettled: () => setLoadingCell(null) }
    );
  }

  return (
    <section className={styles.section} aria-labelledby="team-brand-settings-heading">
      <div className={styles.sectionHeader}>
        <h2 id="team-brand-settings-heading" className={typo.h4}>
          {t('team.brandSettingsSection.title')}
        </h2>
        <p className={styles.sectionDescription}>
          {t(
            isAdmin
              ? 'team.brandSettingsSection.description'
              : 'team.brandSettingsSection.descriptionSelfOnly'
          )}
        </p>
      </div>

      {membersWithBrandRows.length === 0 ? (
        <p className={styles.emptyState}>{t('team.brandSettingsSection.empty')}</p>
      ) : (
        membersWithBrandRows.map((member) => (
          <div key={`brand-settings-${member.user_id}`} className={styles.memberBlock}>
            <div className={styles.memberSubheading}>
              <Typography sizes={['label']}>
                {t('team.brandSettingsSection.memberLabel', {
                  name: member.name,
                  email: member.email
                })}
              </Typography>
            </div>

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">{t('team.brandSettingsSection.columnBrand')}</th>
                    <th scope="col">{t('team.brandSettingsSection.columnEmailNotifications')}</th>
                    <th scope="col">{t('team.brandSettingsSection.columnShowCampaigns')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(member.brand_settings ?? []).map((row) => {
                    const brandLabel = row.name || row.brand_id;
                    const editable = canEditMember(member);
                    const emailKey = cellKey(
                      member.user_id,
                      row.brand_id,
                      'want_email_notifications'
                    );
                    const showKey = cellKey(member.user_id, row.brand_id, 'show_campaigns');
                    return (
                      <tr key={`${member.user_id}-${row.brand_id}`}>
                        <td className={styles.brandCell}>{brandLabel}</td>
                        <td className={styles.checkboxCell}>
                          <div className={styles.cellInner}>
                            {loadingCell === emailKey ? (
                              <Spinner />
                            ) : (
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={row.want_email_notifications}
                                disabled={!editable}
                                onChange={(e) =>
                                  handleToggle(
                                    member,
                                    row.brand_id,
                                    'want_email_notifications',
                                    e.target.checked
                                  )
                                }
                                aria-label={t('team.brandSettingsSection.ariaEmailNotifications', {
                                  brand: brandLabel
                                })}
                              />
                            )}
                          </div>
                        </td>
                        <td className={styles.checkboxCell}>
                          <div className={styles.cellInner}>
                            {loadingCell === showKey ? (
                              <Spinner />
                            ) : (
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                checked={row.show_campaigns}
                                disabled={!editable}
                                onChange={(e) =>
                                  handleToggle(
                                    member,
                                    row.brand_id,
                                    'show_campaigns',
                                    e.target.checked
                                  )
                                }
                                aria-label={t('team.brandSettingsSection.ariaShowCampaigns', {
                                  brand: brandLabel
                                })}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </section>
  );
}
